<template>
  <div
    class="shortcut-tile-card flex flex-col justify-between"
    :class="{ 'has-stats': shortcut.stats && shortcut.stats.length }"
    @click.stop="onClickShortcut()"
  >
    <!-- Top accent border matching shortcut color -->
    <div class="shortcut-accent-bar" :style="{ background: hex_color }"></div>

    <div class="p-4 flex flex-col justify-between flex-1">
      <!-- Main Content Header -->
      <div class="flex items-center justify-between">
        <div class="flex items-center min-w-0 pr-2">
          <!-- Icon Container -->
          <div
            class="shortcut-icon-wrapper flex items-center justify-center rounded-xl mr-3 flex-shrink-0"
            :style="{ background: rgb_color, color: hex_color, border: `1px solid ${rgb_color_border}` }"
          >
            <feather-icon
              :icon="shortcut.icon || 'BoxIcon'"
              svgClasses="h-5 w-5"
              :style="{ color: hex_color }"
            />
          </div>

          <!-- Text Info -->
          <div class="min-w-0">
            <h4 class="shortcut-title font-bold text-base mb-0.5 leading-tight truncate" :title="shortcut.name">
              {{ shortcut.name }}
            </h4>
            <p class="shortcut-desc text-xs truncate" :title="shortcut.description">
              {{ shortcut.description }}
            </p>
          </div>
        </div>

        <!-- Arrow Action Hint -->
        <div class="shortcut-action-hint flex-shrink-0 ml-1">
          <feather-icon icon="ChevronRightIcon" svgClasses="h-4 w-4" />
        </div>
      </div>

      <!-- Stats Section (if any) -->
      <div
        v-if="shortcut.stats && shortcut.stats.length"
        class="shortcut-stats-row"
      >
        <div
          v-for="(item, index) in shortcut.stats"
          :key="index"
          class="shortcut-stat-pill"
        >
          <span class="stat-pill-label">
            {{ item.name }}
          </span>
          <vx-tooltip
            :text="`${item.name}: ${getStatFullValue(item)}`"
            position="top"
            class="stat-pill-tooltip"
          >
            <span class="stat-pill-value" :style="{ color: hex_color }">
              {{ formatStatDisplay(item) }}
            </span>
          </vx-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _color from "@/assets/utils/color.js";
import { RepositoryFactory } from "@/repository/RepositoryFactory";
const ShortcutsRepository = RepositoryFactory.get("shortcuts");

export default {
  name: "ShortcutTile",
  props: {
    shortcut: {
      type: Object,
      default: () => ({ name: "", description: "", color: "#7367f0", url: "", icon: "BoxIcon", stats: [] }),
    },
    index_shortcut: {
      type: Number,
      default: 0,
    },
    is_edit_mode: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    this.loadShortcutDataAsync();
  },
  methods: {
    onClickShortcut() {
      if (this.is_edit_mode) return;
      if (this.shortcut && this.shortcut.url) {
        this.$router.push(`${this.shortcut.url}`).catch(() => {});
      }
    },
    formatStatDisplay(item) {
      if (
        !item ||
        !item.shortCutValue ||
        item.shortCutValue.value === undefined ||
        item.shortCutValue.value === null ||
        item.shortCutValue.value === "" ||
        item.shortCutValue.value === "-"
      ) {
        return "-";
      }

      const rawVal = item.shortCutValue.value;
      const currencyPrefix = item.shortCutValue.currency ? `${item.shortCutValue.currency} ` : "";

      const cleanStr = String(rawVal).replace(/,/g, "").trim();
      const numVal = typeof rawVal === "number" ? rawVal : parseFloat(cleanStr);

      if (isNaN(numVal)) {
        return `${currencyPrefix}${rawVal}`;
      }

      const abs = Math.abs(numVal);

      if (abs >= 1000000000) {
        return `${currencyPrefix}${(numVal / 1000000000).toFixed(2).replace(/\.?0+$/, "")}B`;
      }
      if (abs >= 1000000) {
        return `${currencyPrefix}${(numVal / 1000000).toFixed(2).replace(/\.?0+$/, "")}M`;
      }
      if (abs >= 10000) {
        return `${currencyPrefix}${(numVal / 1000).toFixed(1).replace(/\.?0+$/, "")}k`;
      }

      if (Number.isInteger(numVal)) {
        return `${currencyPrefix}${numVal.toLocaleString()}`;
      }

      return `${currencyPrefix}${numVal.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}`;
    },
    getStatFullValue(item) {
      if (
        !item ||
        !item.shortCutValue ||
        item.shortCutValue.value === undefined ||
        item.shortCutValue.value === null ||
        item.shortCutValue.value === "" ||
        item.shortCutValue.value === "-"
      ) {
        return "-";
      }

      const rawVal = item.shortCutValue.value;
      const currencyPrefix = item.shortCutValue.currency ? `${item.shortCutValue.currency} ` : "";

      const cleanStr = String(rawVal).replace(/,/g, "").trim();
      const numVal = typeof rawVal === "number" ? rawVal : parseFloat(cleanStr);

      if (!isNaN(numVal)) {
        const formatted = numVal.toLocaleString(undefined, {
          minimumFractionDigits: Number.isInteger(numVal) ? 0 : 2,
          maximumFractionDigits: 2,
        });
        return `${currencyPrefix}${formatted}`;
      }

      return `${currencyPrefix}${rawVal}`;
    },
    loadShortcutDataAsync() {
      if (!this.shortcut.stats || this.shortcut.stats.length === 0) {
        return;
      }
      setTimeout(async () => {
        try {
          await Promise.all(
            this.shortcut.stats.map(async (element) => {
              try {
                if (element.endpoint) {
                  const { data } = (await ShortcutsRepository.getShortCutData(element.endpoint)).data;
                  this.$set(element, "shortCutValue", data);
                }
              } catch (error) {
                this.$set(element, "shortCutValue", { value: "-" });
              }
            })
          );
        } catch (e) {
        }
      }, 0);
    },
  },
  computed: {
    rgb_color: function () {
      if (_color.isHexColor(this.shortcut.color)) {
        return _color.getColor(this.shortcut.color, 0.12);
      }
      return _color.getColor("#7367f0", 0.12);
    },
    rgb_color_border: function () {
      if (_color.isHexColor(this.shortcut.color)) {
        return _color.getColor(this.shortcut.color, 0.28);
      }
      return _color.getColor("#7367f0", 0.28);
    },
    hex_color: function () {
      if (_color.isHexColor(this.shortcut.color)) {
        return this.shortcut.color;
      }
      return "#7367f0";
    },
  },
};
</script>

<style lang="scss" scoped>
.shortcut-tile-card {
  position: relative;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.07);
  box-shadow: 0 3px 12px 0 rgba(0, 0, 0, 0.04);
  transition: all 0.22s cubic-bezier(0.16, 1, 0.3, 1);
  cursor: pointer;
  height: 100%;
  min-height: 84px;
  overflow: hidden;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
    border-color: rgba(var(--vs-primary), 0.35);

    .shortcut-action-hint {
      opacity: 1;
      transform: translateX(2px);
      color: rgba(var(--vs-primary), 1);
    }

    .shortcut-icon-wrapper {
      transform: scale(1.06);
    }
  }

  .shortcut-accent-bar {
    height: 3px;
    width: 100%;
    opacity: 0.9;
  }

  .shortcut-icon-wrapper {
    width: 40px;
    height: 40px;
    transition: transform 0.2s ease;
  }

  .shortcut-action-hint {
    opacity: 0.35;
    color: #82868b;
    transition: all 0.2s ease;
  }

  .shortcut-title {
    color: #2c2c2c;
  }

  .shortcut-desc {
    color: #727b87;
    line-height: 1.3;
  }

  .shortcut-stats-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    margin-left: -3px;
    margin-right: -3px;
    gap: 6px;
  }

  .shortcut-stat-pill {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 1 1 0%;
    min-width: 0;
    margin: 0;
    padding: 5px 8px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.035);
    border: 1px solid rgba(0, 0, 0, 0.04);
    overflow: hidden;

    .stat-pill-label {
      color: #7d8793;
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 0.3px;
      text-transform: uppercase;
      margin-right: 6px;
      white-space: nowrap;
      flex-shrink: 0;
    }

    .stat-pill-tooltip {
      min-width: 0;
      display: flex;
      justify-content: flex-end;
      overflow: hidden;
      margin-left: auto;
      max-width: 100%;
    }

    .stat-pill-value {
      font-size: 12px;
      font-weight: 700;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      text-align: right;
      display: block;
      max-width: 100%;
    }
  }
}

/* Dark Theme Support */
::v-deep .theme-dark,
.theme-dark {
  .shortcut-tile-card {
    border-color: rgba(255, 255, 255, 0.08);
    box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.3);
    background-color: #10163a;

    &:hover {
      box-shadow: 0 10px 24px rgba(0, 0, 0, 0.5);
      border-color: rgba(115, 103, 240, 0.5);
    }

    .shortcut-title {
      color: #ebeefd;
    }

    .shortcut-desc {
      color: #9aa2ba;
    }

    .shortcut-action-hint {
      color: #9aa2ba;
      opacity: 0.45;
    }

    .shortcut-stats-row {
      border-top-color: rgba(255, 255, 255, 0.07);
    }

    .shortcut-stat-pill {
      background: rgba(255, 255, 255, 0.05);
      border-color: rgba(255, 255, 255, 0.06);

      .stat-pill-label {
        color: #a3adc2;
      }
    }
  }
}
</style>